import React, { useState } from 'react';
import { FileText, Plus, ChevronRight, Check, X, Sparkles, Loader2 } from 'lucide-react';

/**
 * 3단계 마법사 - 신규 사용자를 위한 문서 처리 가이드
 * 
 * Step 1: 문서 업로드 (첫 페이지 미리보기)
 * Step 2: 템플릿 선택 (신규/기존)
 * Step 3: 필드 확인 후 추출
 */
const DocumentWizard = ({
    isOpen,
    onClose,
    firstPagePreview,  // base64 이미지
    templates,         // 기존 템플릿 목록
    onSelectExisting,  // 기존 템플릿 선택 콜백
    onCreateNew,       // 신규 템플릿 생성 콜백
    onExtract,         // 추출 실행 콜백
    API_BASE_URL
}) => {
    const [step, setStep] = useState(2); // 파일 업로드 후 시작하므로 2부터
    const [mode, setMode] = useState(null); // 'new' | 'existing'
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [suggestedFields, setSuggestedFields] = useState([]);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [templateName, setTemplateName] = useState('');

    // Gemini 2.0 Flash로 필드 추천 받기
    const handleNewDocument = async () => {
        setMode('new');
        setIsAnalyzing(true);

        try {
            const response = await fetch(`${API_BASE_URL}/api/ocr/suggest-fields`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    image: firstPagePreview,
                    model: 'gemini-2.0-flash'
                })
            });

            if (response.ok) {
                const data = await response.json();
                setSuggestedFields(data.fields || []);
            } else {
                // 기본 필드 제안
                setSuggestedFields([
                    { name: '문서명', type: 'string', checked: true },
                    { name: '날짜', type: 'date', checked: true },
                    { name: '금액', type: 'number', checked: false }
                ]);
            }
        } catch (error) {
            console.error('필드 추천 실패:', error);
            setSuggestedFields([
                { name: '필드1', type: 'string', checked: true },
                { name: '필드2', type: 'string', checked: true }
            ]);
        } finally {
            setIsAnalyzing(false);
            setStep(3);
        }
    };

    // 기존 템플릿 선택
    const handleExistingTemplate = () => {
        setMode('existing');
        setStep(3);
    };

    // 필드 체크 토글
    const toggleField = (index) => {
        setSuggestedFields(prev => prev.map((f, i) =>
            i === index ? { ...f, checked: !f.checked } : f
        ));
    };

    // 추출 시작
    const handleExtract = (saveAsTemplate = false) => {
        if (mode === 'existing') {
            onSelectExisting(selectedTemplate);
        } else {
            const fields = suggestedFields.filter(f => f.checked);
            onCreateNew(fields, saveAsTemplate ? templateName : null);
        }
        onExtract();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl w-[800px] max-h-[85vh] overflow-hidden">
                {/* 헤더 */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                            <FileText className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-white">문서 처리 마법사</h2>
                            <p className="text-blue-100 text-sm">3단계로 문서 데이터를 추출하세요</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1 hover:bg-white/10 rounded">
                        <X className="w-5 h-5 text-white" />
                    </button>
                </div>

                {/* 진행 표시 */}
                <div className="px-6 py-4 bg-gray-50 border-b">
                    <div className="flex items-center justify-between">
                        {[
                            { num: 1, label: '문서 업로드', done: true },
                            { num: 2, label: '템플릿 선택' },
                            { num: 3, label: '추출 & 다운로드' }
                        ].map((s, i) => (
                            <React.Fragment key={s.num}>
                                <div className="flex items-center gap-2">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm
                    ${s.done || step > s.num
                                            ? 'bg-green-500 text-white'
                                            : step === s.num
                                                ? 'bg-blue-600 text-white'
                                                : 'bg-gray-200 text-gray-500'}`}>
                                        {s.done || step > s.num ? <Check className="w-4 h-4" /> : s.num}
                                    </div>
                                    <span className={`text-sm font-medium ${step === s.num ? 'text-blue-600' : 'text-gray-500'}`}>
                                        {s.label}
                                    </span>
                                </div>
                                {i < 2 && <ChevronRight className="w-5 h-5 text-gray-300" />}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* 콘텐츠 */}
                <div className="p-6 overflow-y-auto max-h-[calc(85vh-200px)]">
                    {/* Step 2: 템플릿 선택 */}
                    {step === 2 && (
                        <div className="space-y-6">
                            <div className="text-center mb-6">
                                <h3 className="text-xl font-bold text-gray-800">어떤 문서인가요?</h3>
                                <p className="text-gray-500 mt-1">처음 보는 문서인지, 이전에 처리한 문서인지 선택하세요</p>
                            </div>

                            {/* 미리보기 + 선택 */}
                            <div className="grid grid-cols-2 gap-6">
                                {/* 왼쪽: 첫 페이지 미리보기 */}
                                <div className="border rounded-lg overflow-hidden bg-gray-50">
                                    <div className="px-3 py-2 bg-gray-100 border-b text-xs font-medium text-gray-600">
                                        📄 문서 미리보기
                                    </div>
                                    <div className="p-2 h-[300px] overflow-hidden">
                                        {firstPagePreview ? (
                                            <img
                                                src={firstPagePreview}
                                                alt="첫 페이지"
                                                className="w-full h-full object-contain"
                                            />
                                        ) : (
                                            <div className="h-full flex items-center justify-center text-gray-400">
                                                미리보기 없음
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* 오른쪽: 선택 버튼 */}
                                <div className="space-y-4">
                                    {/* 신규 문서 */}
                                    <button
                                        onClick={handleNewDocument}
                                        className="w-full p-5 border-2 rounded-xl text-left hover:border-blue-500 hover:bg-blue-50 transition-all group"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200">
                                                <Plus className="w-6 h-6 text-blue-600" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gray-800 text-lg">🆕 신규 문서</h4>
                                                <p className="text-gray-500 text-sm mt-1">
                                                    처음 보는 문서입니다.<br />
                                                    AI가 필드를 자동으로 추천해드려요.
                                                </p>
                                            </div>
                                        </div>
                                    </button>

                                    {/* 기존 템플릿 */}
                                    <button
                                        onClick={handleExistingTemplate}
                                        disabled={Object.keys(templates || {}).length === 0}
                                        className="w-full p-5 border-2 rounded-xl text-left hover:border-green-500 hover:bg-green-50 transition-all group disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200">
                                                <FileText className="w-6 h-6 text-green-600" />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-gray-800 text-lg">📋 기존 템플릿</h4>
                                                <p className="text-gray-500 text-sm mt-1">
                                                    이전에 저장한 템플릿이 있어요.<br />
                                                    {Object.keys(templates || {}).length}개의 템플릿 사용 가능
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 3: 필드 확인 또는 템플릿 선택 */}
                    {step === 3 && (
                        <div className="space-y-6">
                            {mode === 'new' ? (
                                // 신규: AI 추천 필드
                                <>
                                    <div className="text-center mb-4">
                                        <h3 className="text-xl font-bold text-gray-800 flex items-center justify-center gap-2">
                                            <Sparkles className="w-5 h-5 text-gray-500" />
                                            AI가 추천한 필드
                                        </h3>
                                        <p className="text-gray-500 mt-1">추출할 필드를 선택하세요 (체크 해제로 제외)</p>
                                    </div>

                                    {isAnalyzing ? (
                                        <div className="flex flex-col items-center justify-center py-12">
                                            <Loader2 className="w-10 h-10 text-blue-500 animate-spin mb-3" />
                                            <p className="text-gray-600">Gemini 2.0 Flash가 분석 중...</p>
                                        </div>
                                    ) : (
                                        <>
                                            {/* 필드 목록 */}
                                            <div className="border rounded-lg divide-y max-h-[200px] overflow-y-auto">
                                                {suggestedFields.map((field, idx) => (
                                                    <label
                                                        key={idx}
                                                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={field.checked}
                                                            onChange={() => toggleField(idx)}
                                                            className="w-4 h-4 rounded text-blue-600"
                                                        />
                                                        <span className="flex-1 font-medium text-gray-700">{field.name}</span>
                                                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                                                            {field.type}
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>

                                            {/* 템플릿 저장 옵션 */}
                                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                <label className="flex items-center gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={templateName !== ''}
                                                        onChange={(e) => setTemplateName(e.target.checked ? '새 템플릿' : '')}
                                                        className="w-4 h-4 rounded text-blue-600"
                                                    />
                                                    <span className="text-sm text-gray-700">이 설정을 템플릿으로 저장</span>
                                                </label>
                                                {templateName !== '' && (
                                                    <input
                                                        type="text"
                                                        value={templateName}
                                                        onChange={(e) => setTemplateName(e.target.value)}
                                                        placeholder="템플릿 이름"
                                                        className="mt-2 w-full px-3 py-2 border rounded-lg text-sm"
                                                    />
                                                )}
                                            </div>
                                        </>
                                    )}
                                </>
                            ) : (
                                // 기존: 템플릿 선택
                                <>
                                    <div className="text-center mb-4">
                                        <h3 className="text-xl font-bold text-gray-800">📋 템플릿 선택</h3>
                                        <p className="text-gray-500 mt-1">사용할 템플릿을 선택하세요</p>
                                    </div>

                                    <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                                        {Object.values(templates || {}).map(tpl => (
                                            <label
                                                key={tpl.id}
                                                className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition
                          ${selectedTemplate === tpl.name ? 'bg-blue-50 border-l-4 border-blue-500' : 'hover:bg-gray-50'}`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="template"
                                                    checked={selectedTemplate === tpl.name}
                                                    onChange={() => setSelectedTemplate(tpl.name)}
                                                    className="w-4 h-4 text-blue-600"
                                                />
                                                <div className="flex-1">
                                                    <span className="font-medium text-gray-700">{tpl.name}</span>
                                                    <span className="ml-2 text-xs text-gray-400">
                                                        {tpl.fields?.length || 0}개 필드
                                                    </span>
                                                </div>
                                                <span className="text-xs bg-gray-100 px-2 py-0.5 rounded text-gray-500">
                                                    {tpl.category || '기타'}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* 푸터 */}
                <div className="px-6 py-4 bg-gray-50 border-t flex items-center justify-between">
                    <button
                        onClick={() => step > 2 ? setStep(2) : onClose()}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    >
                        {step > 2 ? '← 이전' : '취소'}
                    </button>

                    {step === 3 && (
                        <button
                            onClick={() => handleExtract(templateName !== '')}
                            disabled={(mode === 'existing' && !selectedTemplate) || isAnalyzing}
                            className="px-6 py-2.5 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            <Check className="w-4 h-4" />
                            추출 시작
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DocumentWizard;
